import axios from '../../api/axiosInstance';
import { STATUS, extractErrorInfo } from './sharedfunction';

const BASE = `${process.env.REACT_APP_API_URL || ''}/usage-log`;

/**
 * Get org-level user usage summary (orgRemaining, orgTotals, userSummary).
 * @param {string} orgId - Organization UUID
 * @param {Object} [params] - Optional query params
 * @param {string} [params.startDate] - YYYY-MM-DD
 * @param {string} [params.endDate] - YYYY-MM-DD
 * @returns {Promise<{ status: number, data?: { orgRemaining, orgTotals, userSummary }, error?: string }>}
 */
export async function getUserSummary(orgId, params = {}) {
  try {
    const searchParams = new URLSearchParams();
    if (params.startDate) searchParams.set('startDate', params.startDate);
    if (params.endDate) searchParams.set('endDate', params.endDate);
    const query = searchParams.toString();
    const url = `${BASE}/org/${orgId}/user-summary${query ? `?${query}` : ''}`;
    const response = await axios.get(url, { withCredentials: true });
    if (response?.data?.success) {
      const data = response?.data?.data;
      return {
        status: STATUS.SUCCESS,
        data: data || { orgRemaining: {}, orgTotals: {}, userSummary: [] },
        error: undefined,
      };
    }
    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error || 'Failed to load user summary',
    };
  } catch (error) {
    return extractErrorInfo(error);
  }
}

/**
 * Get usage history for a specific user in an org.
 * @param {string} orgId - Organization UUID
 * @param {string} userId - User UUID
 * @param {Object} [params] - Optional query params
 * @param {string} [params.startDate] - YYYY-MM-DD
 * @param {string} [params.endDate] - YYYY-MM-DD
 * @returns {Promise<{ status: number, data?: { rows: Array, pagination: { page: number, pageSize: number, total: number } }, error?: string }>}
 */
export async function getUserHistory(orgId, userId, params = {}) {
  try {
    const searchParams = new URLSearchParams();
    if (params.startDate) searchParams.set('startDate', params.startDate);
    if (params.endDate) searchParams.set('endDate', params.endDate);
    if (typeof params.page === 'number') searchParams.set('page', String(params.page));
    if (typeof params.pageSize === 'number') searchParams.set('pageSize', String(params.pageSize));
    const query = searchParams.toString();
    const url = `${BASE}/org/${orgId}/user/${userId}/history${query ? `?${query}` : ''}`;
    const response = await axios.get(url, { withCredentials: true });
    if (response?.data?.success) {
      const data = response?.data?.data;
      return {
        status: STATUS.SUCCESS,
        data: {
          rows: Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : [],
          pagination: data?.pagination || {
            page: params.page || 1,
            pageSize: params.pageSize || 25,
            total: Array.isArray(data?.rows) ? data.rows.length : Array.isArray(data) ? data.length : 0,
          },
        },
        error: undefined,
      };
    }
    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error || 'Failed to load user history',
    };
  } catch (error) {
    return extractErrorInfo(error);
  }
}
